import React, { useState, useEffect } from "react";
import { FaPlus, FaEdit, FaTrash, FaSearch, FaTimes, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import amenitiesService from "../../../services/amenitiesService";
import { getUser } from "../../../services/localService";
import { useAnalytics } from "../../../components/Analytics";
import { AnalyticsEvents, AnalyticsProps } from "../../../utils/analytics";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";


const AmenitiesManagement = () => {
  const [amenities, setAmenities] = useState([]);
  const [filteredAmenities, setFilteredAmenities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [deletingAmenity, setDeletingAmenity] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "" });
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    pageSize: 10,
  });

  const { trackEvent } = useAnalytics();

  useEffect(() => {
    fetchAmenities();
  }, [pagination.currentPage, pagination.pageSize]);

  useEffect(() => {
    filterAmenities();
  }, [amenities, searchTerm]);

  const fetchAmenities = async (pageNumber = pagination.currentPage, pageSize = pagination.pageSize) => {
    try {
      setLoading(true);
      const response = await amenitiesService.getAmenitiesWithPagination(pageNumber, pageSize, "name,asc");

      if (response.status === 200 && response.data) {
        // Handle paginated response structure
        const amenitiesData = response.data.content || [];
        setAmenities(amenitiesData);

        // Update pagination info
        setPagination(prev => ({
          ...prev,
          currentPage: response.data.number || 0,
          totalPages: response.data.totalPages || 0,
          totalElements: response.data.totalElements || 0,
          pageSize: response.data.size || 10,
        }));

        trackAmenitiesEvent("amenities_loaded", {
          count: amenitiesData.length,
          total_elements: response.data.totalElements || 0,
          page: pageNumber,
        });
      } else {
        logger.error("Failed to fetch amenities:", response);
        setAmenities([]);
        const syntheticError = {
          response: {
            status: response?.status || 400,
            data: {
              message: response?.data?.message || "Failed to load amenities",
            },
          },
        };
        showApiMessage(syntheticError, "load amenities");
      }
    } catch (error) {
      logger.error("Error fetching amenities:", error);
      setAmenities([]);
      showApiMessage(error, "load amenities");
    } finally {
      setLoading(false);
    }
  };

  const filterAmenities = () => {
    if (!Array.isArray(amenities)) {
      setFilteredAmenities([]);
      return;
    }

    let filtered = amenities;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        amenity =>
          amenity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (amenity.description && amenity.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Sort by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));

    setFilteredAmenities(filtered);
  };

  const trackAmenitiesEvent = (action, data = {}) => {
    const currentUser = getUser();
    trackEvent(AnalyticsEvents.SETTINGS_ACTION, {
      [AnalyticsProps.ACTION_TYPE]: action,
      [AnalyticsProps.USER_ROLE]: currentUser?.role,
      [AnalyticsProps.USER_ID]: currentUser?.id,
      [AnalyticsProps.COMPONENT_NAME]: "AmenitiesManagement",
      [AnalyticsProps.IS_SUCCESS]: true,
      ...data,
    });
  };

  const handleCreateAmenity = async e => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    try {
      const response = await amenitiesService.createAmenity({
        name: formData.name.trim(),
        description: formData.description.trim() || null,
      });

      if (response.status === 200 || response.status === 201) {
        await fetchAmenities(0); // Reset to first page after creation
        setIsCreateModalOpen(false);
        setFormData({ name: "", description: "" });
        trackAmenitiesEvent("amenity_created", { name: formData.name });
        showApiMessage(response, "create amenity");
      } else {
        const syntheticError = {
          response: {
            status: 400,
            data: { message: "Failed to create amenity" },
          },
        };
        showApiMessage(syntheticError, "create amenity");
      }
    } catch (error) {
      logger.error("Error creating amenity:", error);
      showApiMessage(error, "create amenity");
    }
  };

  const handleEditAmenity = async e => {
    e.preventDefault();
    if (!editingAmenity || !formData.name.trim()) return;

    try {
      setIsUpdating(true);
      const response = await amenitiesService.updateAmenity(editingAmenity.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
      });

      if (response.status === 200) {
        await fetchAmenities();
        setIsEditModalOpen(false);
        setEditingAmenity(null);
        setFormData({ name: "", description: "" });
        trackAmenitiesEvent("amenity_updated", {
          amenity_id: editingAmenity.id,
          name: formData.name,
        });
        showApiMessage(response, "update amenity");
      } else {
        const syntheticError = {
          response: {
            status: 400,
            data: { message: "Failed to update amenity" },
          },
        };
        showApiMessage(syntheticError, "update amenity");
      }
    } catch (error) {
      logger.error("Error updating amenity:", error);
      showApiMessage(error, "update amenity");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAmenity = amenity => {
    setDeletingAmenity(amenity);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteAmenity = async () => {
    if (!deletingAmenity) return;

    try {
      setIsDeleting(true);
      const response = await amenitiesService.deleteAmenity(deletingAmenity.id);

      if (response.status === 200 || response.status === 204) {
        await fetchAmenities();
        trackAmenitiesEvent("amenity_deleted", {
          amenity_id: deletingAmenity.id,
          name: deletingAmenity.name,
        });
        showApiMessage(response, "delete amenity");

        setIsDeleteModalOpen(false);
        setDeletingAmenity(null);
      } else {
        const syntheticError = {
          response: {
            status: 400,
            data: { message: "Failed to delete amenity" },
          },
        };
        showApiMessage(syntheticError, "delete amenity");
      }
    } catch (error) {
      logger.error("Error deleting amenity:", error);
      showApiMessage(error, "delete amenity");
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = amenity => {
    setEditingAmenity(amenity);
    setFormData({
      name: amenity.name,
      description: amenity.description || "",
    });
    setIsEditModalOpen(true);
  };

  const closeModals = () => {
    setIsCreateModalOpen(false);
    setIsEditModalOpen(false);
    setIsDeleteModalOpen(false);
    setEditingAmenity(null);
    setDeletingAmenity(null);
    setFormData({ name: "", description: "" });
  };

  const handlePageChange = newPage => {
    if (newPage >= 0 && newPage < pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const handlePageSizeChange = newPageSize => {
    setPagination(prev => ({
      ...prev,
      pageSize: parseInt(newPageSize),
      currentPage: 0, // Reset to first page when changing page size
    }));
  };

  const PaginationControls = () => (
    <div className="flex flex-col gap-4 justify-between items-center px-4 py-3 bg-gray-50 border-t border-gray-200 sm:flex-row">
      <div className="flex gap-2 items-center text-sm text-gray-600">
        <span>Show</span>
        <select
          value={pagination.pageSize}
          onChange={e => handlePageSizeChange(e.target.value)}
          className="px-2 py-1 text-sm rounded border border-gray-300"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <span>per page</span>
      </div>

      <div className="text-sm text-gray-600">
        Showing {pagination.currentPage * pagination.pageSize + 1} to{" "}
        {Math.min((pagination.currentPage + 1) * pagination.pageSize, pagination.totalElements)} of{" "}
        {pagination.totalElements} results
      </div>

      <div className="flex gap-1 items-center">
        <button
          onClick={() => handlePageChange(pagination.currentPage - 1)}
          disabled={pagination.currentPage === 0}
          className="flex gap-1 items-center px-3 py-1 text-sm rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaChevronLeft className="w-3 h-3" />
          Previous
        </button>

        <div className="flex gap-1 items-center">
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            let pageNum;
            if (pagination.totalPages <= 5) {
              pageNum = i;
            } else if (pagination.currentPage < 3) {
              pageNum = i;
            } else if (pagination.currentPage >= pagination.totalPages - 2) {
              pageNum = pagination.totalPages - 5 + i;
            } else {
              pageNum = pagination.currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`px-3 py-1 text-sm border rounded ${
                  pageNum === pagination.currentPage ? "bg-blue-600 text-white border-blue-600" : "hover:bg-gray-100"
                }`}
              >
                {pageNum + 1}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => handlePageChange(pagination.currentPage + 1)}
          disabled={pagination.currentPage >= pagination.totalPages - 1}
          className="flex gap-1 items-center px-3 py-1 text-sm rounded border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
          <FaChevronRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Amenities Management</h2>
          <p className="text-sm text-gray-600">Manage amenities available for spaces and properties</p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center px-4 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
        >
          <FaPlus className="mr-2 w-4 h-4" />
          Add Amenity
        </button>
      </div>

      {/* Search and basic info */}
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <div className="flex flex-col gap-4 justify-between items-start sm:flex-row sm:items-center">
          <div className="flex-1 max-w-md">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search amenities..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="py-2 pr-4 pl-10 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="text-sm text-gray-600">{pagination.totalElements} total amenities</div>
        </div>
      </div>

      {/* Amenities List */}
      <div className="overflow-hidden bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h3 className="text-sm font-medium text-gray-900">Amenities</h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="w-6 h-6 rounded-full border-b-2 border-blue-600 animate-spin"></div>
            <span className="ml-2 text-sm text-gray-500">Loading...</span>
          </div>
        ) : filteredAmenities.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-gray-500">No amenities found</p>
            {searchTerm && <p className="mt-1 text-xs text-gray-400">Try adjusting your search term</p>}
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-200">
              {filteredAmenities.map(amenity => (
                <div key={amenity.id} className="flex justify-between items-center px-4 py-3 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900">{amenity.name}</div>
                    {amenity.description && (
                      <div className="mt-1 text-xs text-gray-500 truncate">{amenity.description}</div>
                    )}
                  </div>

                  <div className="flex items-center ml-4 space-x-1">
                    <button
                      onClick={() => openEditModal(amenity)}
                      className="p-2 text-gray-400 rounded transition-colors hover:text-blue-600 hover:bg-blue-50"
                      title="Edit amenity"
                    >
                      <FaEdit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteAmenity(amenity)}
                      className="p-2 text-gray-400 rounded transition-colors hover:text-red-600 hover:bg-red-50"
                      title="Delete amenity"
                    >
                      <FaTrash className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {pagination.totalPages > 1 && <PaginationControls />}
          </>
        )}
      </div>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-lg">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Add New Amenity</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateAmenity} className="p-4">
              <div className="space-y-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amenity name"
                    required
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amenity description (optional)"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md transition-colors hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                >
                  Create Amenity
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {isEditModalOpen && editingAmenity && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-lg">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Edit Amenity</h3>
              <button onClick={closeModals} className="text-gray-400 hover:text-gray-600">
                <FaTimes className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleEditAmenity} className="p-4">
              <div className="space-y-4">
                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amenity name"
                    required
                  />
                </div>

                <div>
                  <label className="block mb-1 text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="px-3 py-2 w-full text-sm rounded-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter amenity description (optional)"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex justify-end mt-6 space-x-3">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md transition-colors hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex justify-center items-center px-4 py-2 text-sm text-white bg-blue-600 rounded-md transition-colors hover:bg-blue-700"
                  disabled={isUpdating}
                >
                  {isUpdating ? (
                    <>
                      <svg
                        className="mr-2 w-4 h-4 text-white animate-spin"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </>
                  ) : (
                    "Update Amenity"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && deletingAmenity && (
        <div className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-lg" style={{ borderRadius: "12px" }}>
            <div
              className="flex justify-end p-4 border-none"
              style={{
                padding: "15px 15px 0 15px",
                // minHeight: '40px'
              }}
            >
              <button
                onClick={closeModals}
                className="absolute top-3 right-4 p-0 m-0 text-xl text-black bg-transparent border-none opacity-50 cursor-pointer"
                aria-label="Close"
                disabled={isDeleting}
              >
                ×
              </button>
            </div>
            <div className="text-center" style={{ padding: "5px 0px 24px 20px" }}>
              <h5
                className="mt-3 mb-6 font-normal text-blue-600"
                style={{
                  fontSize: "18px",
                  // marginTop: '10px',
                  marginBottom: "24px",
                }}
              >
                Are you sure you want to delete "{deletingAmenity.name}"?
              </h5>
              <div className="flex gap-3 justify-center">
                <button
                  className="px-6 py-2 w-32 text-blue-600 bg-white rounded-full border border-blue-600"
                  onClick={closeModals}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="flex justify-center items-center px-6 py-2 w-32 text-blue-600 bg-white rounded-full border border-blue-600"
                  onClick={confirmDeleteAmenity}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <div
                      className="w-4 h-4 rounded-full border-2 border-blue-600 animate-spin border-t-transparent"
                      role="status"
                    >
                      <span className="sr-only">Deleting...</span>
                    </div>
                  ) : (
                    "Confirm"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmenitiesManagement;
